import { PermissionOptions } from 'constants/Administrators';
import { Badge, Typography } from 'melp-design/components';
import { Effects, SystemColors } from 'melp-design/style';
import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { NavLink, matchPath, useLocation } from 'react-router-dom';
import { useLinks } from 'config/links';
import { useMe } from 'state/Administrators';
import { Permission } from 'types/Administrators';
import { LinkItem } from 'types/Common';

interface ChildRoute {
  id: string;
  label: string;
  path: string;
  badge?: number;
}

const itemSx = (active: boolean) => ({
  height: 36,
  px: 1.25,
  borderRadius: '8px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  textDecoration: 'none',
  flexShrink: 0,
  color: active ? SystemColors.primary.base : SystemColors.grey[45],
  backgroundColor: active ? SystemColors.primary.tint : 'transparent',
  transition: 'background-color 150ms, color 150ms',
  '&:hover': {
    color: SystemColors.primary.base,
    backgroundColor: SystemColors.primary.tint,
  },
});

const NavItem = ({
  id,
  label,
  path,
  badge,
  active,
  childrenRoutes,
}: {
  id: string;
  label: string;
  path?: string;
  badge?: number;
  active: boolean;
  childrenRoutes: ChildRoute[];
}) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const withChildren = childrenRoutes.length > 0;

  const labelNode = (
    <Typography variant="p1" color="inherit">
      {label}
    </Typography>
  );

  const item = withChildren ? (
    <ButtonBase
      ref={anchorRef}
      onClick={() => setOpen((value) => !value)}
      sx={itemSx(open || active)}
      data-test={id}
    >
      {labelNode}
      <KeyboardArrowDown sx={{ fontSize: 18 }} />
    </ButtonBase>
  ) : (
    <Box
      component={NavLink}
      to={path ?? '#'}
      sx={itemSx(active)}
      data-test={id}
    >
      {labelNode}
    </Box>
  );

  return (
    <>
      <Badge
        badgeContent={badge}
        color="error"
        max={9}
        sx={{ '.MuiBadge-badge': { top: 10, right: 6 } }}
      >
        {item}
      </Badge>
      {withChildren && (
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          transition
          style={{ zIndex: 1100 }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Paper
                variant="outlined"
                sx={{
                  mt: '6px',
                  borderRadius: '8px',
                  boxShadow: Effects.smallElementsShadow,
                }}
              >
                <ClickAwayListener
                  onClickAway={(event) => {
                    if (anchorRef.current?.contains(event.target as Node)) {
                      return;
                    }
                    setOpen(false);
                  }}
                >
                  <MenuList
                    sx={{
                      p: '6px',
                      minWidth: 180,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {childrenRoutes.map((child) => (
                      <MenuItem
                        key={child.path}
                        component={NavLink}
                        to={child.path}
                        data-test={child.id}
                        onClick={() => setOpen(false)}
                        sx={{
                          borderRadius: '8px',
                          py: 1,
                          px: 1.25,
                          color: SystemColors.black,
                          '&:hover': {
                            color: SystemColors.primary.base,
                            backgroundColor: SystemColors.grey[98],
                          },
                          '&.active': {
                            color: SystemColors.primary.base,
                            backgroundColor: SystemColors.primary.tint,
                          },
                        }}
                      >
                        <Badge
                          badgeContent={child.badge}
                          color="error"
                          max={9}
                          sx={{
                            '.MuiBadge-badge': { right: -14 },
                            mr: child.badge ? 2 : 0,
                          }}
                        >
                          <Typography variant="p1" color="inherit">
                            {child.label}
                          </Typography>
                        </Badge>
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </>
  );
};

export const NavMenu = () => {
  const location = useLocation();
  const {
    me,
    getPermissionsByModule,
    isRoot,
    isMelpAdmin,
    isFeatureEnabled,
    hasViewPermission,
  } = useMe();
  const links = useLinks();

  const checkIfPermissionsAllowed = useCallback(
    (permissions: Permission[], matchAll?: boolean) => {
      if (matchAll) {
        const noPermissions = permissions.some(
          (permission: Permission) =>
            permission.permission === PermissionOptions.None,
        );
        return !noPermissions;
      }
      return permissions.some(
        (permission: Permission) =>
          permission.permission === 'view' || permission.permission === 'edit',
      );
    },
    [],
  );

  if (!me) return null;
  if (
    !isFeatureEnabled('advancedPermissions') &&
    !isRoot &&
    !me.permissions?.length &&
    !isMelpAdmin
  ) {
    return null;
  }

  const hasAccessToLink = (link: LinkItem): boolean => {
    const hasCorrectRole = !!me.role && link.roles.includes(me.role);

    if (!hasCorrectRole) {
      return false;
    }

    if (isFeatureEnabled('advancedPermissions')) {
      const childrenLinks = 'links' in link ? link.links : [];

      // A group link is accessible when at least one of its children is.
      if (childrenLinks.length) {
        return childrenLinks.some(hasAccessToLink);
      }

      return hasViewPermission(link.permission);
    }

    if (isRoot) {
      return true;
    }

    if (!link.module) {
      return true;
    }

    const permissions = getPermissionsByModule(link.module);
    const permissionsAllowed = checkIfPermissionsAllowed(
      permissions,
      link.matchAllModules,
    );

    return permissionsAllowed;
  };

  const isPathActive = (path?: string) => {
    if (!path) return false;

    return !!matchPath(location.pathname, {
      path,
      exact: false,
      strict: false,
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        flex: 1,
        minWidth: 0,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {links.filter(hasAccessToLink).map((link) => {
        const childrenLinks = 'links' in link ? link.links : [];

        return (
          <NavItem
            key={link.title}
            id={link.title}
            label={link.title}
            path={link.to}
            badge={link.badge}
            active={
              isPathActive(link.to) ||
              childrenLinks.some((childLink) => isPathActive(childLink.to))
            }
            childrenRoutes={childrenLinks
              .filter(hasAccessToLink)
              .map((childLink) => ({
                id: childLink.title,
                label: childLink.title,
                path: childLink.to,
                badge: childLink.badge,
              }))}
          />
        );
      })}
    </Box>
  );
};
